import React from 'react';
import PropTypes from 'prop-types';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { useDispatch } from 'react-redux';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import Input from '@components/form/Input';
import AddImage from '@/screens/sales-activity/components/todo-list/on-going/image/AddImage';
import BrandSearch from '@components/form/search/BrandSearch';
import ProductSearch from '@components/form/search/ProductSearch';
import Colors from '@global/colors';
import { showImage } from '@/store/global';
import { ARROW_SIZE } from '@/constants';

const IMAGE_SIZE = 100;

const GwpTrackerAccordionItem = ({
  item,
  index,
  isExpanded = false,
  canRemove = true,
  detailData = {},
  detailTodo = {},
  loading = false,
  onToggle = () => {},
  onRemove = () => {},
  onUploading = () => {},
  onChange = () => {},
  onAddImage = () => {},
  onRemoveImage = () => {},
}) => {
  const dispatch = useDispatch();
  const summaryBrand = item?.brand?.name || '-';
  const summaryCampaign = item?.campaign || 'No campaign yet';

  return (
    <View testID={`todo-gwp-tracker-accordion-${index}`} style={styles.card}>
      <View style={styles.headerRow}>
        <TouchableOpacity
          testID={`todo-gwp-tracker-accordion-toggle-${index}`}
          activeOpacity={0.8}
          style={styles.headerMain}
          onPress={onToggle}>
          <View style={styles.badge}>
            <SociollaText type="bold" style={styles.badgeText}>
              #{index + 1}
            </SociollaText>
          </View>
          <View style={styles.headerTextWrap}>
            <SociollaText type="bold" numberOfLines={1} style={styles.title}>
              {summaryCampaign}
            </SociollaText>
            <SociollaText numberOfLines={1} style={styles.subtitle}>
              Brand: {summaryBrand}
            </SociollaText>
          </View>
          <View
            style={[
              styles.arrowWrap,
              { transform: [{ rotate: isExpanded ? '180deg' : '0deg' }] },
            ]}>
            <Image
              style={ARROW_SIZE}
              width={ARROW_SIZE.width}
              height={ARROW_SIZE.height}
              svg
              source={require('@assets/img/icons/arrow-down-grey.svg')}
            />
          </View>
        </TouchableOpacity>

        {canRemove ? (
          <TouchableOpacity
            testID={`todo-gwp-tracker-accordion-remove-${index}`}
            activeOpacity={0.7}
            style={styles.removeBtn}
            onPress={onRemove}>
            <Image
              style={styles.removeIcon}
              width={12}
              height={12}
              svg
              source={require('@assets/img/icons/close-grey.svg')}
            />
          </TouchableOpacity>
        ) : null}
      </View>

      {isExpanded ? (
        <View style={styles.content}>
          <View style={styles.formSection}>
            <BrandSearch
              name="brands"
              isRequired
              value={item?.brand || {}}
              onChange={(value) =>
                onChange({
                  brand: { id: value?.id, name: value?.name },
                  product: {},
                })
              }
            />
          </View>

          <View style={styles.formSection}>
            <ProductSearch
              isRequired
              editable={Boolean(item?.brand?.id)}
              value={item?.product || {}}
              labelKey="reference"
              customFilter={{
                'brand.id': item?.brand?.id,
                classification: 'gwp_non_sellable',
              }}
              onChange={(value) =>
                onChange({
                  product: {
                    id: value?.id,
                    combination_id: value?.product_combination_id,
                    variant: value?.attributes,
                    reference: value?.reference,
                    name: value?.name,
                    image: value?.image,
                  },
                })
              }
            />
          </View>

          <View style={styles.formSection}>
            <Input
              label="Campaign"
              placeholder="Campaign"
              multiline
              value={item?.campaign || ''}
              editable={!loading}
              onChangeText={(val) => onChange({ campaign: val })}
            />
          </View>

          <View style={styles.formSection}>
            <Input
              label="Campaign Detail"
              placeholder="Campaign Detail"
              multiline
              value={item?.campaign_detail || ''}
              editable={!loading}
              onChangeText={(val) => onChange({ campaign_detail: val })}
            />
          </View>

          <View style={styles.inlineRow}>
            <View style={styles.inlineField}>
              <Input
                label="GWP Stock"
                placeholder="0"
                value={item?.gwp_stock || ''}
                editable={!loading}
                isNumeric
                keyboardType="numeric"
                onChangeText={(val) => onChange({ gwp_stock: val })}
              />
            </View>
            <View style={styles.inlineField}>
              <Input
                label="GWP Out"
                placeholder="0"
                value={item?.gwp_out || ''}
                editable={!loading}
                isNumeric
                keyboardType="numeric"
                onChangeText={(val) => onChange({ gwp_out: val })}
              />
            </View>
          </View>

          <View style={styles.formSection}>
            <View style={styles.imageHeader}>
              <SociollaText style={styles.imageLabel}>
                Receipt Photos
              </SociollaText>
              <SociollaText style={styles.imageCount}>
                {(item?.images || []).length}/10
              </SociollaText>
            </View>

            <View style={styles.imageGrid}>
              {(item?.images || []).map((imgData, imgIndex) => {
                const displayUri =
                  typeof imgData === 'string'
                    ? imgData
                    : imgData?.uri || imgData?.url || '';
                return (
                  <View
                    key={`${displayUri}-${imgIndex}`}
                    style={styles.imageCell}>
                    <TouchableOpacity
                      testID={`todo-gwp-tracker-accordion-image-${imgIndex}`}
                      activeOpacity={0.8}
                      onPress={() =>
                        dispatch(
                          showImage({
                            isModal: true,
                            url: { uri: displayUri },
                          }),
                        )
                      }>
                      <Image
                        style={styles.image}
                        width={IMAGE_SIZE}
                        height={IMAGE_SIZE}
                        source={{ uri: displayUri }}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      testID={`todo-gwp-tracker-accordion-image-remove-${imgIndex}`}
                      activeOpacity={0.7}
                      style={styles.imageRemoveBtn}
                      onPress={() => onRemoveImage(imgIndex)}>
                      <Image
                        style={styles.removeIcon}
                        width={12}
                        height={12}
                        svg
                        source={require('@assets/img/icons/close-grey.svg')}
                      />
                    </TouchableOpacity>
                  </View>
                );
              })}

              {(item?.images || []).length < 10 ? (
                <View style={styles.imageCell}>
                  <AddImage
                    data={item?.images || []}
                    setData={(val) => onAddImage(val)}
                    loadingCallback={onUploading}
                    disabled={loading}
                    additional={{
                      activity: detailData,
                      task: detailTodo,
                      showImageOption: true,
                      isMultipleImage: true,
                      limit: 10,
                    }}
                  />
                </View>
              ) : null}
            </View>
          </View>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    width: '100%',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.grey0,
    backgroundColor: Colors.white,
    marginBottom: 12,
    overflow: 'hidden',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: Colors.white,
  },
  headerMain: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  badge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.grey16,
    borderWidth: 1,
    borderColor: Colors.grey0,
    marginRight: 10,
  },
  badgeText: {
    fontSize: 11,
    color: Colors.grey2,
  },
  headerTextWrap: {
    flex: 1,
    paddingRight: 6,
  },
  title: {
    fontSize: 13,
    color: Colors.grey2,
  },
  subtitle: {
    fontSize: 11,
    color: Colors.grey1,
    marginTop: 2,
  },
  arrowWrap: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.grey5,
    marginLeft: 8,
  },
  removeIcon: {
    width: 12,
    height: 12,
  },
  content: {
    paddingHorizontal: 12,
    paddingBottom: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.grey0,
  },
  formSection: {
    marginTop: 10,
  },
  inlineRow: {
    flexDirection: 'row',
    marginTop: 10,
    marginHorizontal: -4,
  },
  inlineField: {
    flex: 1,
    paddingHorizontal: 4,
  },
  imageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  imageLabel: {
    fontSize: 11,
    color: Colors.grey2,
  },
  imageCount: {
    fontSize: 11,
    color: Colors.grey1,
  },
  imageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -6,
    alignItems: 'flex-start',
  },
  imageCell: {
    marginHorizontal: 6,
    marginBottom: 10,
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: 6,
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: Colors.grey16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
  },
  imageRemoveBtn: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    right: 4,
    top: 4,
    backgroundColor: Colors.white,
  },
});

GwpTrackerAccordionItem.propTypes = {
  item: PropTypes.object,
  index: PropTypes.number,
  isExpanded: PropTypes.bool,
  canRemove: PropTypes.bool,
  detailData: PropTypes.object,
  detailTodo: PropTypes.object,
  loading: PropTypes.bool,
  onToggle: PropTypes.func,
  onRemove: PropTypes.func,
  onUploading: PropTypes.func,
  onChange: PropTypes.func,
  onAddImage: PropTypes.func,
  onRemoveImage: PropTypes.func,
};

export default React.memo(GwpTrackerAccordionItem);
